import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "@lynx/ui-components";

import { accountThunks } from "actions-thunks/account-thunks";
import { setWireImageAC } from "actions-thunks/actions";
import { supportTableUtils } from "ui/ui-kit/react-table/supportTableUtils";
import BankAccountCard from "./components/BankAccountCard";
import {
  formatWireAmount,
  formatWireDate,
  getPayoutDeclineReason,
  getPayoutSnapshot,
  getWireCreatedDate,
  getWireStatusDisplay,
  getWireTypeDisplay,
  getWireUpdatedDate,
  isPendingPayout,
} from "./wireUtils";

import "./payout-modals.scss";
import "./wire-details-modal.scss";

const WireDetailsModal = ({ wire, onClose }) => {
  const dispatch = useDispatch();
  const { currentAccount, wireImage } = useSelector((state) => state.accounts);
  const { loading } = useSelector((state) => state.utils);
  const [imageLoading, setImageLoading] = useState(false);

  const { isPayout, isAuto, label: displayType } = getWireTypeDisplay(wire);
  const isDeposit = !isPayout;
  const {
    text: statusText,
    iconPath: statusIconPath,
    className: statusClass,
  } = getWireStatusDisplay(wire);

  // Prepared for the backend: snapshot + decline reason are not in the wire list yet
  const snapshot = isPayout ? getPayoutSnapshot(wire) : null;
  const declineReason = isPayout ? getPayoutDeclineReason(wire) : "";

  const declaredAmount = wire?.amount?.pending || wire?.amount?.claimed || 0;
  const receivedAmount = wire?.amount?.received || 0;
  const hasConfirmation =
    isDeposit && wire?.claim?.confirmation && Object.keys(wire.claim.confirmation).length > 0;

  // Fetch wire image if confirmation exists
  useEffect(() => {
    if (hasConfirmation && wire?._id && currentAccount?._id) {
      setImageLoading(true);
      dispatch(accountThunks.getWireConfirmationImageTC(currentAccount._id, wire._id))
        .finally(() => setImageLoading(false));
    }
    return () => {
      // Clean up wire image when modal closes
      dispatch(setWireImageAC(null));
    };
  }, [hasConfirmation, wire?._id, currentAccount?._id, dispatch]);

  const isPDF = wireImage?.image?.split("/")[1]?.split(";")[0] === "pdf";

  const handleDownloadImage = () => {
    if (!wireImage?.image) return;

    const link = document.createElement("a");
    link.href = wireImage.image;
    link.download = `wire-confirmation-${wire._id}.${isPDF ? "pdf" : "jpg"}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    dispatch(setWireImageAC(null));
    onClose();
  };

  const createdDate = getWireCreatedDate(wire);
  // resolved pay-outs without an explicit update date show the creation date
  const updatedDate = getWireUpdatedDate(wire) || (isPayout ? createdDate : null);
  const formatDate = (value) =>
    isPayout
      ? formatWireDate(value, { monthOnly: isAuto })
      : supportTableUtils.tableTimeFormat(value);
  // A pending pay-out has not been touched yet: no "Updated" row
  const showUpdated = !(isPayout && isPendingPayout(wire));

  return (
    <div className="modal-window">
      <Modal
        className="wire-details-modal cabinet-modals"
        styleType="zimtra-primary"
        isOpen={true}
        hideBlur
        hasCloseIcon={true}
        onClickOutside={handleClose}
      >
        <Modal.Header
          className="modal-inner-header"
          onClose={handleClose}
          title={<h2 className="zimtra-h2">Transaction Details</h2>}
        />
        <Modal.Body className="modal-inner-body">
          <div className="wire-details-content">
            <div className="detail-row">
              <div className="detail-label">Type:</div>
              <div className="detail-value">{displayType}</div>
            </div>

            {isDeposit ? (
              <>
                <div className="detail-row">
                  <div className="detail-label">Amount (declared):</div>
                  <div className="detail-value">{formatWireAmount(declaredAmount)}</div>
                </div>
                {receivedAmount > 0 && (
                  <div className="detail-row">
                    <div className="detail-label">Amount (received):</div>
                    <div className="detail-value">{formatWireAmount(receivedAmount)}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="detail-row">
                <div className="detail-label">Amount:</div>
                <div className="detail-value">{formatWireAmount(declaredAmount)}</div>
              </div>
            )}

            <div className="detail-row">
              <div className="detail-label">Status:</div>
              <div className={`detail-value status-cell-with-icon ${statusClass}`}>
                {statusIconPath && (
                  <img src={statusIconPath} alt={statusText} className="status-icon" />
                )}
                {statusText}
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Created:</div>
              <div className="detail-value">{createdDate ? formatDate(createdDate) : "N/A"}</div>
            </div>

            {showUpdated && (
              <div className="detail-row">
                <div className="detail-label">Updated:</div>
                <div className="detail-value">{updatedDate ? formatDate(updatedDate) : "N/A"}</div>
              </div>
            )}

            {snapshot && (
              <div className="detail-row bank-account-row">
                <BankAccountCard bank={snapshot} className="bank-account-card-plain" />
              </div>
            )}

            {declineReason && (
              <div className="detail-row decline-reason-row">
                <div className="decline-reason-text">{declineReason}</div>
              </div>
            )}

            {hasConfirmation && (
              <div className="detail-row attachment-row">
                <div className="detail-label">Attachment:</div>
                <div className="detail-value">
                  {imageLoading || loading ? (
                    <span>Loading...</span>
                  ) : wireImage?.image ? (
                    <button
                      type="button"
                      className="download-attachment-button"
                      onClick={handleDownloadImage}
                    >
                      {`wire-confirmation-${wire._id}.${isPDF ? "pdf" : "jpg"}`}
                    </button>
                  ) : (
                    <span>Attachment available</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WireDetailsModal;
